import { Component, OnInit } from '@angular/core';
import {
  DialogService,
  EventAggregator,
  Navigator,
  SearchConfig,
  tr,
  Types,
  ViewType,
  SingleKeyNavigationHelper,
  KeyBase,
  DialogResult,
  OverviewMain,
  AuthService,
} from '@kpd/framework-ngx';
import { UserFacade } from 'app/shared/services/facades/user.facade';
import { Tenant } from 'app/shared/models/tenant';
import { TenantService } from 'app/shared/services/tenant.service';
import { UserTypeService } from 'app/shared/services/user-type.service';
import { Observable, of } from 'rxjs';
import { Permissions } from 'app/shared/services/permissions';
import { map } from 'rxjs/operators';
import { UMLocalizationService } from 'app/shared/services/localization.service';

@Component({
  selector: 'app-users-overview',
  templateUrl: './users-overview.component.html',
})
export class UsersOverviewComponent extends OverviewMain implements OnInit {
  Permissions: typeof Permissions = Permissions;
  columnsConfig = [
    { field: 'upn', header: tr('Upn'), width: '30%', type: 'link' },
    { field: 'displayName', header: tr('Name'), width: '25%' },
    { field: 'type', header: tr('Type'), width: '20%' },
    { field: 'language', header: tr('Language'), width: '15%' },
  ];
  searchConfig = new SearchConfig({
    multipleCriteria: true,
    fields: [
      { field: 'upn', displayValue: tr('Upn'), dataType: Types.String },
      { field: 'firstName', displayValue: tr('First name'), dataType: Types.String },
      { field: 'lastName', displayValue: tr('Last name'), dataType: Types.String },
      { field: 'displayName', displayValue: tr('Name'), dataType: Types.String },
      {
        field: 'culture',
        displayValue: tr('Language'),
        dataType: Types.Entity,
        entityConfig: {
          displayField: 'display',
          lookupField: 'name',
          completeMethod: (value: string) => {
            const cultures = UMLocalizationService.getCultures();
            const culturesArray = Object.keys(cultures).map((key) => {
              return { name: key, display: cultures[key] };
            });
            if (!value) {
              return of(culturesArray);
            }
            return of(
              culturesArray.filter((culture: any) => {
                return culture.display.toUpperCase().includes(value.toUpperCase());
              }),
            );
          },
        },
      },
      {
        field: 'typeAsArray',
        displayValue: tr('Type'),
        dataType: Types.Array,
        entityConfig: {
          displayField: 'name',
          lookupField: 'id',
          completeMethod: (value: string) => {
            if (!value) {
              return of(this.userTypeService.getUserTypes());
            }
            return of(
              this.userTypeService.getUserTypes().filter((userType) => {
                return userType.name.toUpperCase().includes(value.toUpperCase());
              }),
            );
          },
        },
      },
    ],
  });
  viewType = ViewType.Table;
  ViewTypes = ViewType;
  selectedItemIds: KeyBase[] = [];
  navigatorHelper: SingleKeyNavigationHelper;
  tenant: Tenant = new Tenant();

  get hasSystemUserManagePermission(): boolean {
    return this.authService.getIdentity().hasPermission(Permissions.SystemUserManage);
  }

  get isSystemUserSelected(): Observable<boolean> {
    return this.entityFacade.selectedItems$.pipe(map((items) => items.some((i) => i.isSystemUser)));
  }

  constructor(
    navigator: Navigator,
    public entityFacade: UserFacade,
    public eventAggregator: EventAggregator,
    private dialogService: DialogService,
    private tenantService: TenantService,
    private userTypeService: UserTypeService,
    private authService: AuthService,
  ) {
    super();
    this.navigatorHelper = new SingleKeyNavigationHelper('users', navigator);
    if (this.hasSystemUserManagePermission) {
      this.columnsConfig = [
        ...this.columnsConfig,
        { field: 'isSystemUser', header: tr('System User'), width: '10%', type: 'boolean' },
      ];
      this.searchConfig.fields = [
        ...this.searchConfig.fields,
        { field: 'isSystemUser', displayValue: tr('System User'), dataType: Types.Boolean },
      ];
    }
  }

  ngOnInit(): void {
    super.ngOnInit();

    this.tenantService.getTenant().subscribe((tenant: Tenant) => {
      this.tenant = tenant;
    });

    this.entityFacade.viewType$.subscribe((result) => {
      this.viewType = result;
    });
  }

  onDelete(): void {
    const message = tr('Are you sure to delete one (or more) selected users?');
    this.dialogService
      .open('delete', {
        message,
        header: tr('Delete user(s)'),
        verifyButtonText: tr('Delete'),
        verifyButtonStyle: 'danger',
        cancelButtonText: tr('Cancel'),
      })
      .afterClosed()
      .subscribe((result: DialogResult) => {
        if (result === DialogResult.Verify) {
          this.entityFacade.deleteItems(this.selectedItemIds).subscribe();
        }
      });
  }

  onToggleViewType(): void {
    this.entityFacade.changeViewType(this.viewType === ViewType.Table ? ViewType.Card : ViewType.Table);
  }
}
